import React, { useState, useEffect, useRef } from 'react';
import { Input, Select, Row, Col, Card, Avatar, Button, Spin, Empty, Tag, Divider } from 'antd';
import { SearchOutlined, UserOutlined, ThunderboltOutlined } from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import api from '../api/client';

import { levelFromCurrentLevel } from '../utils/levelMap';

export default function Search() {
  const { user } = useUser();
  const studentLevel = user?.role === 'student' ? levelFromCurrentLevel(user?.current_level) : '';

  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [searched, setSearched]     = useState(false);
  const [name, setName]             = useState('');
  const [gender, setGender]         = useState('');
  const [level, setLevel]           = useState(studentLevel);
  const [subject, setSubject]       = useState('');
  const [ageMin, setAgeMin]         = useState('');
  const [ageMax, setAgeMax]         = useState('');

  // Recommendation
  const [subjectTypes, setSubjectTypes]       = useState([]);
  const [recSubjectKey, setRecSubjectKey]     = useState(null);
  const [recResults, setRecResults]           = useState([]);
  const [recLoading, setRecLoading]           = useState(false);
  const [recSearched, setRecSearched]         = useState(false);

  const navigate  = useNavigate();
  const isFirst   = useRef(true);

  useEffect(() => {
    api.get('/api/gate/subject-types').then(r => setSubjectTypes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; doSearch(); return; }
    const timer = setTimeout(doSearch, 400);
    return () => clearTimeout(timer);
  }, [name, subject, gender, level, ageMin, ageMax]);

  const doSearch = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = { role: 'tutor' };
      if (name)    params.name    = name;
      if (gender)  params.gender  = gender;
      if (level)   params.level   = level;
      if (subject) params.subject = subject;
      if (ageMin)  params.age_min = ageMin;
      if (ageMax)  params.age_max = ageMax;
      const res = await api.get('/api/users/search', { params });
      setResults(res.data);
    } catch { setResults([]); }
    finally { setLoading(false); }
  };

  const doRecommend = async () => {
    if (!recSubjectKey) return;
    setRecLoading(true);
    setRecSearched(true);
    try {
      const res = await api.get('/api/users/recommend', { params: { subject_key: recSubjectKey } });
      setRecResults(res.data);
    } catch { setRecResults([]); }
    finally { setRecLoading(false); }
  };

  const hasFilters = !!(name || gender || level || subject || ageMin || ageMax);

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Find a tutor</h1>
        <p>Browse Singapore tutors across PSLE, O-Level, JC and IB. Click any card to view their profile.</p>
      </div>

      {/* ── Search filters ── */}
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '14px 16px', marginBottom: 24 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search by name..."
              prefix={<SearchOutlined style={{ color: '#ccc' }} />}
              value={name} onChange={e => setName(e.target.value)} allowClear
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select style={{ width: '100%' }} placeholder="Gender" allowClear
              value={gender || undefined} onChange={v => setGender(v || '')}>
              <Select.Option value="Male">Male</Select.Option>
              <Select.Option value="Female">Female</Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select style={{ width: '100%' }} placeholder="Level" allowClear
              value={level || undefined} onChange={v => setLevel(v || '')}>
              <Select.Option value="O-Level">O-Level</Select.Option>
              <Select.Option value="JC">JC / A-Level</Select.Option>
              <Select.Option value="IP">IP</Select.Option>
              <Select.Option value="PSLE">PSLE</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Input placeholder="Subject" value={subject}
              onChange={e => setSubject(e.target.value)} allowClear />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Input placeholder="Min age" type="number" min={1}
              value={ageMin} onChange={e => setAgeMin(e.target.value || '')}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Input placeholder="Max age" type="number" min={1}
              value={ageMax} onChange={e => setAgeMax(e.target.value || '')}
            />
          </Col>
          <Col xs={24} sm={4} md={2}>
            <Button block disabled={!hasFilters}
              onClick={() => { setName(''); setGender(''); setLevel(''); setSubject(''); setAgeMin(''); setAgeMax(''); }}
              style={hasFilters ? { background: '#0a0a0a', color: '#fff', border: 'none' } : {}}>
              Clear
            </Button>
          </Col>
        </Row>
      </div>

      {/* Search results */}
      {!loading && searched && results.length > 0 && (
        <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
          {results.length} tutor{results.length !== 1 ? 's' : ''} found
        </p>
      )}
      {loading && <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>}
      {!loading && searched && results.length === 0 && (
        <Empty description="No tutors found" style={{ padding: 60 }} />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
        {results.map(u => (
          <Col xs={24} sm={12} md={8} key={u.user_id}>
            <TutorCard tutor={u} onClick={() => navigate(`/tutor/${u.user_id}`)} />
          </Col>
        ))}
      </Row>

      {/* ── Recommendation ── */}
      <Divider />
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 4px' }}>
          <ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
          Get a recommendation
        </h2>
        <p style={{ color: '#888', fontSize: 13, margin: 0 }}>
          Pick a subject and we'll match you with tutors who teach it.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <Select
          style={{ width: 320 }}
          size="large"
          placeholder="Select a subject..."
          value={recSubjectKey || undefined}
          onChange={setRecSubjectKey}
          showSearch
          optionFilterProp="label"
          options={(studentLevel
            ? subjectTypes.filter(st => st.level === studentLevel)
            : subjectTypes
          ).map(st => ({
            value: st.subject_key,
            label: studentLevel ? st.display_name : `${st.display_name} (${st.level})`,
          }))}
        />
        <Button type="primary" size="large" loading={recLoading}
          style={{ background: '#0a0a0a', border: 'none' }}
          disabled={!recSubjectKey}
          onClick={doRecommend}>
          Find tutors
        </Button>
      </div>

      {recLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
      {!recLoading && recSearched && recResults.length === 0 && (
        <Empty description="No tutors found for this subject" style={{ padding: 40 }} />
      )}
      {!recLoading && recResults.length > 0 && (
        <>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>
            {recResults.length} tutor{recResults.length !== 1 ? 's' : ''} teach this subject
          </p>
          <Row gutter={[16, 16]}>
            {recResults.map(u => (
              <Col xs={24} sm={12} md={8} key={`${u.user_id}_${u.subject_name}`}>
                <TutorCard tutor={u} onClick={() => navigate(`/tutor/${u.user_id}`)}
                  badge={`${u.subject_name} · ${u.level}${u.monthly_rate ? ` · $${parseFloat(u.monthly_rate).toFixed(0)}/month` : ''}`}
                  studentCount={u.student_count}
                />
              </Col>
            ))}
          </Row>
        </>
      )}
    </AppLayout>
  );
}

function TutorCard({ tutor: u, onClick, badge, studentCount }) {
  return (
    <Card variant="outlined" hoverable style={{ borderColor: '#e8e8e8', cursor: 'pointer', height: '100%' }} onClick={onClick}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <Avatar size={52}
          src={u.user_avatar_link || undefined}
          icon={!u.user_avatar_link ? <UserOutlined /> : undefined}
          style={{ background: '#0a0a0a', flexShrink: 0, fontSize: 18 }}>
          {!u.user_avatar_link && u.name?.[0]}
        </Avatar>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#0a0a0a', marginBottom: 2 }}>{u.name}</div>
          {u.gender && <span style={{ fontSize: 11, color: '#999' }}>{u.gender}{u.age ? ` · ${u.age} yrs` : ''}</span>}
          {badge && (
            <div style={{ marginTop: 6 }}>
              <Tag color="blue" style={{ fontSize: 11 }}>{badge}</Tag>
              {studentCount > 0 && <Tag style={{ fontSize: 11 }}>{studentCount} student{studentCount !== 1 ? 's' : ''}</Tag>}
            </div>
          )}
          {u.subjects?.length > 0 && !badge && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
              {u.subjects.slice(0, 3).map((s, i) => (
                <Tag key={i} style={{ fontSize: 10 }}>{s.subject_name}</Tag>
              ))}
            </div>
          )}
          {u.description && (
            <p style={{ fontSize: 12, color: '#666', margin: '8px 0 0', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {u.description}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
